import { Link } from 'react-router-dom';

import { useLanguage } from '@/components/LanguageContext';
import LandingShellV3 from '@/components/landing/v3/LandingShellV3';
import { urlDe } from '@/lib/rutasPublicas';
import { pickLandingV3Content } from '@/pages/landingV3.content';

/**
 * NotFound404 — la página que se sirve con un 404 de verdad.
 *
 * ── POR QUÉ SUSTITUYE A `lib/PageNotFound.jsx` EN VEZ DE ARREGLARLO ─────────
 *
 * Porque aquel NO SE PUEDE prerenderizar, y no por un detalle: importaba
 * `@/api/base44Client`, que a su vez carga `mockClient.js`, que llama a
 * `localStorage.getItem()` **en ámbito de módulo**. En Node eso es un
 * `ReferenceError` al importarlo, antes de pintar nada. Además usaba `useQuery`
 * y `window.location`, que tampoco existen en el build.
 *
 * Y de paso se van dos cosas que llevaban tiempo en producción:
 *
 *  - Un «Admin Note» de la plantilla de Base44 que decía, en inglés y a los
 *    usuarios con rol admin, *«This could mean that the AI hasn't implemented
 *    this page yet. Ask it to implement it in the chat.»*.
 *  - El pathname crudo en pantalla («The page "xyz" could not be found»). A
 *    quien se ha equivocado de enlace no le sirve de nada leerlo, y se lo
 *    enseña a cualquiera que provoque un 404 a propósito.
 *
 * La anterior estaba entera en inglés, sin una sola clave de i18n.
 *
 * ── POR QUÉ LLEVA EL NAV Y EL PIE ───────────────────────────────────────────
 *
 * Un 404 sin salida es un callejón: la página vieja solo tenía un botón «Go
 * Home» que hacía `window.location.href` —recarga completa— y nada más. Con
 * `LandingShellV3` el visitante tiene el menú entero, y los enlaces a las
 * páginas de producto le dan una segunda oportunidad de encontrar lo que
 * buscaba.
 *
 * ── EL `<head>` NO SALE DE AQUÍ ─────────────────────────────────────────────
 *
 * `LandingShellV3` llama a `useSeoHead(paginaDeLaRuta(pathname))`, y para una
 * url que no existe eso es `null`: no pone título ni canonical, que es
 * exactamente lo que se quiere. El título y el `noindex` los escribe el
 * prerender en `dist/404.html` a partir de `lib/rutasSueltas.js`.
 *
 * Un 404 con canonical sería un error: le estaría diciendo a Google que esa url
 * inexistente es la versión buena de algo.
 */
export default function NotFound404() {
  const { t, language } = useLanguage();
  const { nav } = pickLandingV3Content(language);

  /*
   * Las cuatro de producto más Multiubicaciones. Salen del MENÚ y no de una
   * lista escrita aquí: si mañana se añade una página al menú aparece sola, y
   * si se renombra, el nombre la sigue.
   */
  const sugerencias = [
    ...nav.producto.items.map((i) => ({ clave: i.clave, titulo: i.titulo })),
    { clave: 'multiubicaciones', titulo: nav.multiubicaciones },
  ];

  return (
    <LandingShellV3>
      <section className="px-6 pt-[76px] pb-[96px]">
        <div className="max-w-[640px] mx-auto text-center">
          <p className="text-[64px] leading-none font-bold text-v3-line select-none">404</p>

          <h1 className="text-[28px] md:text-[34px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink mt-4">
            {t('notFound.heading')}
          </h1>

          <p className="text-[16px] leading-[1.65] text-v3-body mt-4">
            {t('notFound.body')}
          </p>

          <Link
            to={urlDe('home', language)}
            className="inline-flex items-center justify-center mt-8 px-6 py-3 rounded-full bg-v3-ink text-white text-[15px] font-semibold hover:opacity-90 transition-opacity"
          >
            {t('notFound.cta')}
          </Link>

          <div className="mt-12 pt-8 border-t border-v3-line text-left">
            <p className="text-[13px] font-semibold uppercase tracking-[.08em] text-v3-body">
              {t('notFound.linksTitle')}
            </p>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {sugerencias.map((s) => (
                <li key={s.clave}>
                  <Link
                    to={urlDe(s.clave, language)}
                    className="block py-2 text-[15.5px] text-v3-ink hover:underline"
                  >
                    {s.titulo}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </LandingShellV3>
  );
}
